/**
 * Resolutions Store — manages resolution list, pending-vote count, and CRUD.
 * Domain: Resolutions & Voting
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'

export const useResolutionsStore = defineStore('resolutions', () => {
  // ─── State ───────────────────────────────────────────────────────────────
  const items = ref([])
  const loading = ref(false)
  const pendingVoteCount = ref(0)

  // Filters
  const searchQuery   = ref('')
  const statusFilter  = ref('')
  const typeFilter    = ref('')

  // ─── Getters ─────────────────────────────────────────────────────────────
  const filtered = computed(() => {
    let list = items.value
    if (searchQuery.value) {
      const q = searchQuery.value.toLowerCase()
      list = list.filter(r =>
        r.title?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
      )
    }
    if (statusFilter.value) list = list.filter(r => r.status === statusFilter.value)
    if (typeFilter.value)   list = list.filter(r => r.type   === typeFilter.value)
    return list
  })

  const hasPendingVotes = computed(() => pendingVoteCount.value > 0)

  // ─── Actions ─────────────────────────────────────────────────────────────
  const fetchAll = async (params = {}) => {
    loading.value = true
    try {
      const response = await api.get('/resolutions', { params })
      items.value = response.data.resolutions || []
      return items.value
    } catch (error) {
      console.error('Failed to fetch resolutions:', error)
      return []
    } finally {
      loading.value = false
    }
  }

  const fetchById = async (id) => {
    try {
      const response = await api.get(`/resolutions/${id}`)
      return response.data
    } catch (error) {
      console.error(`Failed to fetch resolution ${id}:`, error)
      throw error
    }
  }

  const create = async (data) => {
    try {
      const response = await api.post('/resolutions', data)
      const newItem = response.data.resolution
      items.value.unshift(newItem)
      return { success: true, resolution: newItem }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to create resolution' }
    }
  }

  const update = async (id, data) => {
    try {
      const response = await api.put(`/resolutions/${id}`, data)
      const updated = response.data.resolution
      const idx = items.value.findIndex(r => r.id === id)
      if (idx !== -1) items.value[idx] = updated
      return { success: true, resolution: updated }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to update resolution' }
    }
  }

  const remove = async (id) => {
    try {
      await api.delete(`/resolutions/${id}`)
      items.value = items.value.filter(r => r.id !== id)
      return { success: true }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Failed to delete resolution' }
    }
  }

  const castVote = async (resolutionId, voteData) => {
    try {
      const response = await api.post(`/resolutions/${resolutionId}/vote`, voteData)
      await refreshPendingVoteCount()
      return { success: true, data: response.data }
    } catch (error) {
      return { success: false, message: error.response?.data?.message || 'Vote failed' }
    }
  }

  /** Recalculates how many voting resolutions the current user hasn't voted on yet. */
  const refreshPendingVoteCount = async () => {
    const authStore = useAuthStore()
    const currentUserId = authStore.user?.id
    if (!currentUserId) { pendingVoteCount.value = 0; return }

    try {
      const votingResponse = await api.get('/resolutions?status=voting')
      const votingResolutions = votingResponse.data.resolutions || []
      let count = 0

      for (const resolution of votingResolutions) {
        try {
          const detail = await api.get(`/resolutions/${resolution.id}`)
          const votes  = detail.data.votes || []
          if (!votes.find(v => v.user_id === currentUserId)) count++
        } catch {
          count++ // assume pending if details unavailable
        }
      }

      pendingVoteCount.value = count
    } catch (error) {
      console.error('Failed to refresh pending vote count:', error)
      pendingVoteCount.value = 0
    }
  }

  return {
    // State
    items, loading, pendingVoteCount, searchQuery, statusFilter, typeFilter,
    // Getters
    filtered, hasPendingVotes,
    // Actions
    fetchAll, fetchById, create, update, remove, castVote, refreshPendingVoteCount
  }
})
